/* eslint-disable react-hooks/exhaustive-deps */
import { IconButton, InputAdornment } from '@ethings/ui-toolkit';
import { OutlinedInput } from '@mui/material';
import { SxProps } from '@mui/system';
import { debounce } from 'lodash';
import { forwardRef, KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOOKUP_DEBOUNCE_TIME } from '../../constants';
import useSmallScreen from '../../hooks/use-small-screen';
import { useSearch } from '../../pages/list/use-search';
import { trackEvent } from '../../util';
import { Icon } from '../icons';
import { useSearchStyles } from './styles';

interface Props {
  id?: string;
  disabled?: boolean;
  focus?: boolean;
  onChanged?: (text: string) => unknown;
  onCloseClicked?: () => unknown;
  onEnterPressed?: (highlightIndex: number) => unknown;
  search?: string;
  sx?: SxProps;
}

export const Search = forwardRef<HTMLDivElement, Props>(({
  disabled = false,
  focus,
  id,
  onCloseClicked,
  onEnterPressed,
  sx,
}, ref) => {
  const { classes } = useSearchStyles();
  const smallScreen = useSmallScreen();
  const searchInput = useRef<HTMLInputElement>(null);
  const { t } = useTranslation(['common']);
  const {
    highlightIndex,
    isLoading,
    pagination,
    setHighlightIndex,
    setPagination,
    setText,
    result,
    text,
  } = useSearch();
  const [localText, setLocalText] = useState(text);

  useEffect(() => {
    setLocalText(text);
  }, [text]);

  const onKeyDown = useCallback((event: ReactKeyboardEvent): void => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        setText?.('');
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isLoading) break;
        setHighlightIndex(
          highlightIndex === undefined || highlightIndex < 1
            ? undefined
            : highlightIndex - 1,
        );
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (isLoading || !result || !result.itemCount) break;
        if (highlightIndex === undefined || highlightIndex < result.itemCount - 1) {
          setHighlightIndex((highlightIndex ?? -1) + 1);
        }
        break;
      case 'PageUp':
        event.preventDefault();
        if (pagination.skip <= 0) return;
        if (pagination.skip < pagination.limit) {
          setPagination({ ...pagination, skip: 0 });
          return;
        }
        setPagination({ ...pagination, skip: pagination.skip - pagination.limit });
        break;
      case 'PageDown':
        event.preventDefault();
        if (isLoading || !result || result.matchCount < pagination.skip + pagination.limit) return;
        setPagination({ ...pagination, skip: pagination.skip + pagination.limit });
        break;
    }
  }, [highlightIndex, result, pagination, searchInput, isLoading]);

  const onKeyUp = useCallback((event: ReactKeyboardEvent): void => {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        if (smallScreen) {
          onCloseClicked?.();
          break;
        }
        if (isLoading || highlightIndex === undefined) break;
        onEnterPressed?.(highlightIndex);
        break;
    }
  }, [highlightIndex, smallScreen]);

  const keyDownHandler = useCallback((event: KeyboardEvent): void => {
    switch (event.key) {
      case 'S':
        if (event.shiftKey && event.altKey) {
          if (searchInput && searchInput.current) searchInput.current.focus();
        }
        break;
    }
  }, [searchInput]);

  const handleClear = (): void => {
    setText('');
    setLocalText('');
  };

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler);

    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, [keyDownHandler]);

  const debouncedSetText = useMemo(() => debounce((text: string) => {
    setText?.(text);
  }, LOOKUP_DEBOUNCE_TIME), [setText]);

  return (
    <OutlinedInput
      ref={ref}
      data-testid="search-bar"
      autoFocus={focus}
      classes={{ notchedOutline: classes.outlinedInput }}
      disabled={disabled}
      inputProps={{ 'data-testid': 'search-bar-input' }}
      inputRef={searchInput}
      onChange={(e) => {
        setLocalText(e.target.value);
        debouncedSetText(e.target.value);
      }}
      onKeyDown={!smallScreen ? onKeyDown : undefined}
      onKeyUp={onKeyUp}
      onFocus={() => {
        if (id) {
          trackEvent('keyword_search', 'focus_input', `${id}_list`);
        }
      }}
      placeholder={t('common:component.lookup.hint.placeholder')}
      startAdornment={
        <InputAdornment position="start" data-testid="search-button">
          <Icon icon="SearchIcon" style={{ fontSize: '1.25rem' }} data-testid="search-icon" />
        </InputAdornment>
      }
      endAdornment={
        <InputAdornment position="end">
          {localText && (
            <IconButton aria-label="clear" data-testid="clear-button" onClick={handleClear} edge="end">
              <Icon icon="CloseIcon" style={{ fontSize: '1.25rem' }} data-testid="clear-icon" />
            </IconButton>
          )}
        </InputAdornment>
      }
      sx={{
        '& .MuiOutlinedInput-input': { padding: '0.657rem 0.5rem 0.657rem 0' },
        width: smallScreen ? '100%' : '23ch',
        ...sx,
      }}
      type="text"
      value={localText}
    />
  );
});

Search.displayName = 'Search';
